import React, { useState, useMemo, useEffect } from "react";
import {
  Eye,
  Download,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  X,
  Trash2,
  FileText,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  formatCurrency,
  formatDate,
  subscriptionAPI,
  bondsAPI,
  filesAPI,
  allocationRequestsAPI,
  locTemplatesAPI,
  walletAPI,
} from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../../components/common/LoadingSpinner";

const SubscriptionRequests = () => {
  const { getDisplayName, user } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [subscriptionBondId, setSubscriptionBondId] = useState("");
  const [subscriptionPrimarySubscriberId, setSubscriptionPrimarySubscriberId] =
    useState("");
  const [subscriptionAmount, setSubscriptionAmount] = useState("");
  const [selectedWalletId, setSelectedWalletId] = useState("");
  const [locPreviewLoading, setLocPreviewLoading] = useState(false);
  const [locDownloadLoading, setLocDownloadLoading] = useState(false);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [locTemplate, setLocTemplate] = useState(null);
  const [locTemplateLoading, setLocTemplateLoading] = useState(false);
  const [locFieldData, setLocFieldData] = useState({});
  const [primarySubscribers, setPrimarySubscribers] = useState([]);
  const [primarySubscribersLoading, setPrimarySubscribersLoading] =
    useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestDetails, setShowRequestDetails] = useState(false);
  const [submissionDownloadId, setSubmissionDownloadId] = useState(null);
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [approvalConfirmation, setApprovalConfirmation] = useState(null);
  const EMPIRICAL_DAY_MS = 24 * 60 * 60 * 1000;

  const { data: availableBondsResp, isPending: availableBondsLoading } =
    useQuery({
      queryKey: ["investor-available-bonds"],
      queryFn: async () => {
        const resp = await subscriptionAPI.getAvailableBondsForInvestors();
        return resp.data;
      },
      staleTime: 60_000,
    });

  const {
    data: allocationRequestsData = [],
    isPending: requestsLoading,
    error: requestsError,
  } = useQuery({
    queryKey: ["investor-allocation-requests"],
    queryFn: async () => {
      const resp = await allocationRequestsAPI.getMyRequests();
      const payload = resp?.data?.data;
      if (Array.isArray(payload?.requests)) return payload.requests;
      if (Array.isArray(payload)) return payload;
      return [];
    },
    staleTime: 30_000,
  });

  const { data: bondDocumentsData, isPending: bondDocumentsLoading } = useQuery(
    {
      queryKey: ["investor-bond-documents", subscriptionBondId],
      queryFn: async () => {
        try {
          const resp = await bondsAPI.getBondDocuments(subscriptionBondId);
          const payload = resp?.data?.data;
          const docs = Array.isArray(payload?.documents)
            ? payload.documents
            : Array.isArray(payload)
              ? payload
              : [];
          return docs;
        } catch (error) {
          console.error("Failed to fetch bond documents", error);
          return [];
        }
      },
      enabled: !!subscriptionBondId && showSubscribeModal,
      staleTime: 60_000,
    }
  );

  const {
    data: pendingChangeRequestsData = [],
    isPending: changeRequestsLoading,
  } = useQuery({
    queryKey: ["investor-pending-change-requests"],
    queryFn: async () => {
      try {
        const resp = await allocationRequestsAPI.getMyPendingChangeRequests();
        const payload = resp?.data?.data;
        return Array.isArray(payload?.changeRequests)
          ? payload.changeRequests
          : [];
      } catch (error) {
        console.error("Failed to fetch pending change requests", error);
        return [];
      }
    },
    staleTime: 30_000,
  });

  const { data: myWalletsData, isPending: walletsLoading } = useQuery({
    queryKey: ["investor-my-wallets"],
    enabled: showSubscribeModal,
    queryFn: async () => {
      try {
        const resp = await walletAPI.getMyWallets();
        return resp?.data;
      } catch (error) {
        console.error("Failed to fetch investor wallets", error);
        return { wallets: [] };
      }
    },
    staleTime: 60_000,
  });

  const createRequestMutation = useMutation({
    mutationFn: (payload) => allocationRequestsAPI.createRequest(payload),
    onSuccess: () => {
      toast.success("Subscription request submitted.");
      queryClient.invalidateQueries(["investor-allocation-requests"]);
      handleCloseSubscriptionModal();
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit allocation request.";
      toast.error(message);
    },
    onSettled: () => {
      setIsSubmittingRequest(false);
    },
  });

  const approveChangeRequestMutation = useMutation({
    mutationFn: (changeRequestId) =>
      allocationRequestsAPI.approveChangeRequest(changeRequestId),
    onSuccess: () => {
      toast.success("Amount change approved successfully.");
      queryClient.invalidateQueries(["investor-pending-change-requests"]);
      queryClient.invalidateQueries(["investor-allocation-requests"]);
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to approve change request.";
      toast.error(message);
    },
  });

  const rejectChangeRequestMutation = useMutation({
    mutationFn: (changeRequestId) =>
      allocationRequestsAPI.rejectChangeRequest(changeRequestId),
    onSuccess: () => {
      toast.success("Amount change rejected.");
      queryClient.invalidateQueries(["investor-pending-change-requests"]);
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to reject change request.";
      toast.error(message);
    },
  });

  const deleteRequestMutation = useMutation({
    mutationFn: (requestId) => allocationRequestsAPI.deleteRequest(requestId),
    onSuccess: () => {
      toast.success("Allocation request deleted successfully.");
      queryClient.invalidateQueries(["investor-allocation-requests"]);
      setDeleteConfirmation(null);
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete request.";
      toast.error(message);
      setDeleteConfirmation(null);
    },
  });

  const approveL1Mutation = useMutation({
    mutationFn: (requestId) =>
      allocationRequestsAPI.approveInvestorL1(requestId),
    onSuccess: () => {
      toast.success("L1 approval successful. Request moved to Pending L2.");
      queryClient.invalidateQueries(["investor-allocation-requests"]);
      setApprovalConfirmation(null);
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to approve L1.";
      toast.error(message);
      setApprovalConfirmation(null);
    },
  });

  const approveL2Mutation = useMutation({
    mutationFn: (requestId) =>
      allocationRequestsAPI.approveInvestorL2(requestId),
    onSuccess: () => {
      toast.success(
        "L2 approval successful. Request submitted to Primary Subscriber."
      );
      queryClient.invalidateQueries(["investor-allocation-requests"]);
      setApprovalConfirmation(null);
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to approve L2.";
      toast.error(message);
      setApprovalConfirmation(null);
    },
  });

  useEffect(() => {
    setLocPreviewLoading(false);
    setLocDownloadLoading(false);
  }, [subscriptionBondId, showSubscribeModal]);

  const normalizeBondType = (productType, bondName = "") =>
    /sukuk|islamic/i.test(String(productType || bondName || ""))
      ? "Islamic"
      : "Traditional";

  const pendingRequests = useMemo(() => {
    const list = Array.isArray(allocationRequestsData)
      ? allocationRequestsData
      : [];
    return list.map((req) => {
      const submissions = Array.isArray(req.submissions) ? req.submissions : [];
      const latestSubmission =
        submissions.length > 0 ? submissions[submissions.length - 1] : null;
      const snapshot = req.bondSnapshot || {};
      const rawStatus = (req.status || "submitted").toLowerCase();
      return {
        type: "request",
        id: `request-${req._id}`,
        requestId: req._id,
        bondId: req.bondId,
        bondName: snapshot.bondName || "Unknown Bond",
        issuer: snapshot.issuer || "—",
        issuerId: snapshot.issuerId || null,
        isin: snapshot.isin || "—",
        stockCode: snapshot.stockCode || "—",
        bondType: normalizeBondType(snapshot.productType, snapshot.bondName),
        amount: req.totalRequestedAmount || 0,
        allocatedAmount: req.allocatedAmount ?? null,
        currentValue: req.totalRequestedAmount || 0,
        status: rawStatus,
        statusLabel: rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1),
        latestSubmissionAt:
          req.latestSubmissionAt || latestSubmission?.submittedAt,
        submissions,
        custodianId: req.custodianId || null,
        bondSnapshot: snapshot,
        issueDate: snapshot.issueDate || null,
        maturityDate: snapshot.maturityDate || null,
        couponRate: snapshot.couponRate ?? null,
        profitRate: snapshot.profitRate ?? null,
        l1ApprovedBy: req.l1ApprovedBy || null,
        l2ApprovedBy: req.l2ApprovedBy || null,
        l1ApprovedAt: req.l1ApprovedAt || null,
        l2ApprovedAt: req.l2ApprovedAt || null,
        rejectedAt: req.rejectedAt || null,
        rejectionReason: req.rejectionReason || null,
      };
    });
  }, [allocationRequestsData]);

  const requestsData = useMemo(() => {
    return [...pendingRequests].sort((a, b) => {
      const aTime = a.latestSubmissionAt
        ? new Date(a.latestSubmissionAt).getTime()
        : 0;
      const bTime = b.latestSubmissionAt
        ? new Date(b.latestSubmissionAt).getTime()
        : 0;
      return bTime - aTime;
    });
  }, [pendingRequests]);

  const investorWallets = useMemo(() => {
    const root = myWalletsData?.data || myWalletsData;
    const walletsArray = Array.isArray(root?.wallets)
      ? root.wallets
      : Array.isArray(root)
        ? root
        : [];

    return walletsArray
      .filter(Boolean)
      .map((wallet) => ({
        ...wallet,
        id: String(wallet._id || wallet.id || wallet.walletId || ""),
      }))
      .filter((wallet) => wallet.id);
  }, [myWalletsData]);

  const selectedWallet = useMemo(() => {
    if (!selectedWalletId) return null;
    return (
      investorWallets.find((wallet) => wallet.id === selectedWalletId) || null
    );
  }, [investorWallets, selectedWalletId]);

  useEffect(() => {
    if (!showSubscribeModal) {
      return;
    }

    if (investorWallets.length === 1) {
      setSelectedWalletId(investorWallets[0].id);
      return;
    }

    if (
      selectedWalletId &&
      !investorWallets.some((wallet) => wallet.id === selectedWalletId)
    ) {
      setSelectedWalletId("");
    }
  }, [investorWallets, selectedWalletId, showSubscribeModal]);

  useEffect(() => {
    setPage(1);
  }, [pageSize, requestsData.length]);

  const totalItems = requestsData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const displayedRequests = requestsData.slice(startIndex, endIndex);

  const isLoading = requestsLoading;
  const loadError = requestsError || null;

  const getStatusBadge = (status) => {
    const normalizedStatus = (status || "").toLowerCase().replace(/_/g, " ");
    const badgeConfig = {
      active: { class: "badge-success", label: "Active" },
      pending: { class: "badge-warning", label: "Pending" },
      "pending l1": { class: "badge-warning", label: "Pending L1" },
      "pending l2": { class: "badge-warning", label: "Pending L2" },
      submitted: { class: "badge-info", label: "Submitted" },
      approved: { class: "badge-success", label: "Approved" },
      allocated: { class: "badge-success", label: "Allocated" },
      rejected: { class: "badge-error", label: "Rejected" },
      matured: { class: "badge-secondary", label: "Matured" },
      confirmed: { class: "badge-success", label: "Confirmed" },
    };
    const config = badgeConfig[normalizedStatus] || {
      class: "badge-secondary",
      label: status || "Unknown",
    };
    return <span className={`badge ${config.class}`}>{config.label}</span>;
  };

  const getApprovalStatusBadge = (request) => {
    if (request.l2ApprovedBy) {
      return <span className="badge badge-success">L2 Approved</span>;
    }
    if (request.l1ApprovedBy) {
      return <span className="badge badge-info">L1 Approved</span>;
    }
    return <span className="badge badge-warning">Pending L1</span>;
  };

  const getBondTypeDisplay = (bondType) => {
    return bondType === "Islamic" ? "Sukuk" : "Conventional Bond";
  };

  const getRateValue = (holding) => {
    if (holding.bondType === "Islamic") {
      return holding.profitRate != null
        ? holding.profitRate
        : holding.couponRate;
    }
    return holding.couponRate != null ? holding.couponRate : holding.profitRate;
  };

  const handleViewRequestDetails = (request) => {
    setSelectedRequest(request);
    setShowRequestDetails(true);
  };

  const handleCloseRequestDetails = () => {
    setShowRequestDetails(false);
    setSelectedRequest(null);
    setSubmissionDownloadId(null);
  };

  const handleDeleteRequest = (request) => {
    setDeleteConfirmation({
      requestId: request.requestId,
      bondName: request.bondName,
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmation?.requestId) {
      await deleteRequestMutation.mutateAsync(deleteConfirmation.requestId);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation(null);
  };

  const handleApprovalRequest = (request, level) => {
    setApprovalConfirmation({
      requestId: request.requestId,
      bondName: request.bondName,
      level,
    });
  };

  const handleConfirmApproval = async () => {
    if (!approvalConfirmation) return;

    if (approvalConfirmation.level === "L1") {
      await approveL1Mutation.mutateAsync(approvalConfirmation.requestId);
    } else if (approvalConfirmation.level === "L2") {
      await approveL2Mutation.mutateAsync(approvalConfirmation.requestId);
    }
  };

  const handleCancelApproval = () => {
    setApprovalConfirmation(null);
  };

  const canDeleteRequest = (request) => {
    return ["pending_l1", "pending_l2", "submitted"].includes(request.status);
  };

  const canApproveL1 = (request) => {
    if (!user?.id) return false;
    if (request.status !== "pending_l1") return false;

    const l1ApproverId = request.l1ApprovedBy?._id || request.l1ApprovedBy;
    if (l1ApproverId && String(l1ApproverId) === String(user.id)) {
      return false;
    }

    return true;
  };

  const canApproveL2 = (request) => {
    if (!user?.id) return false;
    if (request.status !== "pending_l2") return false;

    const l1ApproverId = request.l1ApprovedBy?._id || request.l1ApprovedBy;
    if (l1ApproverId && String(l1ApproverId) === String(user.id)) {
      return false;
    }

    const l2ApproverId = request.l2ApprovedBy?._id || request.l2ApprovedBy;
    if (l2ApproverId && String(l2ApproverId) === String(user.id)) {
      return false;
    }

    return true;
  };

  const handleDownloadSubmissionLoc = async (submission) => {
    if (!submission?.locFileId || submissionDownloadId) {
      return;
    }

    try {
      setSubmissionDownloadId(submission.locFileId);
      const response = await filesAPI.downloadFile(submission.locFileId);
      const blob =
        response?.data instanceof Blob
          ? response.data
          : new Blob([response?.data], {
            type:
              response?.headers?.["content-type"] ||
              "application/octet-stream",
          });
      const url = window.URL.createObjectURL(blob);
      const fallbackName =
        selectedRequest?.bondName?.replace(/\s+/g, "_") || "loc_document";
      const submissionDate = submission.submittedAt
        ? new Date(submission.submittedAt).toISOString().split("T")[0]
        : "submission";
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fallbackName}_${submissionDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download LOC submission", error);
      toast.error("Unable to download LOC document. Please try again.");
    } finally {
      setSubmissionDownloadId(null);
    }
  };

  const handlePreviewSubmissionLoc = async (submission) => {
    if (!submission?.locFileId) {
      return;
    }

    try {
      const response = await filesAPI.downloadFile(submission.locFileId);
      const blob =
        response?.data instanceof Blob
          ? response.data
          : new Blob([response?.data], {
            type:
              response?.headers?.["content-type"] ||
              "application/octet-stream",
          });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      console.error("Failed to preview LOC submission", error);
      toast.error("Unable to preview LOC document. Please try again.");
    }
  };

  const eligibleBonds = useMemo(() => {
    const list = Array.isArray(availableBondsResp?.data)
      ? availableBondsResp.data
      : Array.isArray(availableBondsResp?.bonds)
        ? availableBondsResp.bonds
        : Array.isArray(availableBondsResp)
          ? availableBondsResp
          : [];
    const now = Date.now();
    return list.filter((bond) => {
      const startDate = bond.subscriptionStartDate || bond.subscriptionStart;
      const endDate = bond.subscriptionEndDate || bond.subscriptionEnd;
      if (!startDate || !endDate) return false;
      const start = new Date(startDate).getTime();
      const end = new Date(endDate).getTime() + (EMPIRICAL_DAY_MS - 1);
      if (Number.isNaN(start) || Number.isNaN(end)) return false;
      return now >= start && now <= end;
    });
  }, [availableBondsResp]);

  useEffect(() => {
    const fetchPrimarySubscribers = async () => {
      if (!subscriptionBondId) {
        setPrimarySubscribers([]);
        setSubscriptionPrimarySubscriberId("");
        return;
      }

      try {
        setPrimarySubscribersLoading(true);
        const response =
          await locTemplatesAPI.getPrimarySubscribersWithTemplates(
            subscriptionBondId
          );
        if (response.data.success) {
          setPrimarySubscribers(response.data.data.primarySubscribers || []);
        } else {
          setPrimarySubscribers([]);
        }
      } catch (error) {
        console.error("Error fetching primary subscribers:", error);
        setPrimarySubscribers([]);
      } finally {
        setPrimarySubscribersLoading(false);
      }
    };

    fetchPrimarySubscribers();
  }, [subscriptionBondId]);

  useEffect(() => {
    const loadTemplate = async () => {
      if (!subscriptionBondId || !subscriptionPrimarySubscriberId) {
        setLocTemplate(null);
        setLocFieldData({});
        return;
      }

      try {
        setLocTemplateLoading(true);
        const response = await locTemplatesAPI.getTemplateByBondAndPS(
          subscriptionBondId,
          subscriptionPrimarySubscriberId
        );
        if (response.data.success) {
          const template = response.data.data || response.data;
          setLocTemplate(template);
          const initialData = {};
          (template.fields || []).forEach((field) => {
            if (field.defaultValue !== undefined) {
              initialData[field.name] = field.defaultValue;
            }
          });
          setLocFieldData(initialData);
        } else {
          setLocTemplate(null);
          setLocFieldData({});
        }
      } catch (error) {
        console.error("Error loading template:", error);
        setLocTemplate(null);
        setLocFieldData({});
        toast.error("Failed to load LOC template. Please try again.");
      } finally {
        setLocTemplateLoading(false);
      }
    };

    loadTemplate();
  }, [subscriptionBondId, subscriptionPrimarySubscriberId]);

  const handleFieldChange = (fieldName, value) => {
    setLocFieldData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handlePreviewPDF = async () => {
    if (!locTemplate || pdfGenerating) return;

    const missingFields = (locTemplate.fields || [])
      .filter(
        (field) =>
          field.required &&
          (!locFieldData[field.name] || locFieldData[field.name] === "")
      )
      .map((field) => field.label);

    if (missingFields.length > 0) {
      toast.error(
        `Please fill in required fields: ${missingFields.join(", ")}`
      );
      return;
    }

    try {
      setPdfGenerating(true);
      const response = await locTemplatesAPI.generatePDF(
        locTemplate._id || locTemplate.id,
        locFieldData
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPdfPreviewUrl(url);

      window.open(url, "_blank", "noopener");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF preview. Please try again.");
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!locTemplate || pdfGenerating) return;

    const missingFields = (locTemplate.fields || [])
      .filter(
        (field) =>
          field.required &&
          (!locFieldData[field.name] || locFieldData[field.name] === "")
      )
      .map((field) => field.label);

    if (missingFields.length > 0) {
      toast.error(
        `Please fill in required fields: ${missingFields.join(", ")}`
      );
      return;
    }

    try {
      setPdfGenerating(true);
      const response = await locTemplatesAPI.generatePDF(
        locTemplate._id || locTemplate.id,
        locFieldData
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `LOC_${locTemplate.bondId?.bondName || "document"}_${new Date().toISOString().split("T")[0]
        }.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleCloseSubscriptionModal = () => {
    setShowSubscribeModal(false);
    setSubscriptionBondId("");
    setSubscriptionPrimarySubscriberId("");
    setSubscriptionAmount("");
    setSelectedWalletId("");
    setHasAgreedToTerms(false);
    setIsSubmittingRequest(false);
    setLocTemplate(null);
    setLocFieldData({});
    setPrimarySubscribers([]);
    setPdfPreviewUrl(null);
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
    }
  };

  const buildWalletSnapshotFromSelection = (wallet) => {
    if (!wallet) return null;

    const bankDetails = wallet.bankAccountDetails || {};
    const walletId = wallet._id || wallet.id || wallet.walletId;
    const leiCandidates = [
      bankDetails.lei,
      wallet.lei,
      user?.lei,
      user?.corporateInfo?.lei,
      user?.corporateInfo?.LEI,
    ].filter((value) => typeof value === "string" && value.trim().length > 0);
    const leiValue =
      leiCandidates.length > 0
        ? leiCandidates[0].trim().toUpperCase()
        : undefined;
    const accountTypeRaw = bankDetails.accountType || wallet.accountType;
    const normalizedAccountType = accountTypeRaw
      ? accountTypeRaw.toLowerCase()
      : undefined;

    if (!walletId || !wallet.walletAddress) {
      return null;
    }

    return {
      walletId: String(walletId),
      walletAddress: wallet.walletAddress,
      accountType: normalizedAccountType,
      bankName: bankDetails.bankName || wallet.bankName || undefined,
      accountName: bankDetails.accountName || wallet.accountName || undefined,
      accountNumber:
        bankDetails.accountNumber || wallet.accountNumber || undefined,
      accountCategory:
        bankDetails.accountCategory || wallet.accountCategory || undefined,
      swiftBic: bankDetails.swiftBic || wallet.swiftBic || undefined,
      branchCode: bankDetails.branchCode || wallet.branchCode || undefined,
      lei: leiValue,
    };
  };

  const handleSubmitSubscription = async () => {
    if (isSubmittingRequest || createRequestMutation.isPending) {
      return;
    }

    if (
      !subscriptionBondId ||
      !subscriptionAmount ||
      !subscriptionPrimarySubscriberId
    ) {
      toast.error("Please complete all fields before submitting.");
      return;
    }

    if (!locTemplate) {
      toast.error("Please wait for LOC template to load.");
      return;
    }

    if (!selectedWallet) {
      toast.error("Please select the bank account to fund this request.");
      return;
    }

    const walletSnapshot = buildWalletSnapshotFromSelection(selectedWallet);

    if (!walletSnapshot) {
      toast.error(
        "Selected bank account is missing required wallet information."
      );
      return;
    }

    if (!hasAgreedToTerms) {
      toast.error("Please agree to the terms and conditions.");
      return;
    }

    const missingFields = (locTemplate.fields || [])
      .filter(
        (field) =>
          field.required &&
          (!locFieldData[field.name] || locFieldData[field.name] === "")
      )
      .map((field) => field.label);

    if (missingFields.length > 0) {
      toast.error(
        `Please fill in required fields: ${missingFields.join(", ")}`
      );
      return;
    }

    const amountValue = Number(subscriptionAmount);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      toast.error("Please enter a valid bond amount.");
      return;
    }

    try {
      setIsSubmittingRequest(true);

      const pdfResponse = await locTemplatesAPI.generatePDF(
        locTemplate._id || locTemplate.id,
        locFieldData
      );

      const blob = new Blob([pdfResponse.data], { type: "application/pdf" });
      const pdfFile = new File(
        [blob],
        `LOC_${locTemplate.bondId?.bondName || "document"}_${new Date().toISOString().split("T")[0]
        }.pdf`,
        { type: "application/pdf" }
      );

      const formData = new FormData();
      formData.append("file", pdfFile);
      formData.append("type", "document");
      formData.append(
        "relatedTo",
        JSON.stringify({ module: "bonds", id: subscriptionBondId })
      );
      formData.append(
        "metadata",
        JSON.stringify({
          category: "locSubmission",
          bondId: subscriptionBondId,
        })
      );

      const uploadResp = await filesAPI.uploadFile(formData);
      const filePayload =
        uploadResp?.data?.data?.file ||
        uploadResp?.data?.file ||
        uploadResp?.data;
      const locFileId = filePayload?._id || filePayload?.id;
      if (!locFileId) {
        throw new Error("Failed to upload LOC document.");
      }

      const primarySubscriberIdValue =
        subscriptionPrimarySubscriberId &&
          subscriptionPrimarySubscriberId.trim() !== ""
          ? subscriptionPrimarySubscriberId.trim()
          : null;

      await createRequestMutation.mutateAsync({
        bondId: subscriptionBondId,
        primarySubscriberId: primarySubscriberIdValue,
        amount: amountValue,
        locFileId,
        selectedWallet: walletSnapshot,
      });
    } catch (error) {
      console.error("Subscription submission failed", error);
      toast.error("Failed to submit subscription request. Please try again.");
      setIsSubmittingRequest(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="py-12 text-center card-body">
            <p className="mb-4 text-error-600">
              Failed to load subscription requests.
            </p>
            <button
              onClick={() =>
                queryClient.invalidateQueries(["investor-allocation-requests"])
              }
              className="btn-primary"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Submit LOC</h1>
              <p className="mt-1 text-secondary-600">
                Submit and manage your Letter of Commitment requests
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowSubscribeModal(true)}

              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              title="Subscribe to Bond"
            >
              Subscribe to Bond
            </button>
          </div>

          {/* Pending Amount Change Requests */}
          {pendingChangeRequestsData.length > 0 && (
            <div className="border-yellow-200 card bg-yellow-50">
              <div className="card-header bg-amber-50">
                <h3 className="text-lg font-semibold text-yellow-900">
                  Pending Amount Change Requests
                </h3>
                <p className="mt-1 text-sm text-yellow-700">
                  Facility Agent has requested to change LOC amounts. Please
                  review and approve or reject.
                </p>
              </div>
              <div className="p-0 card-body">
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead className="bg-yellow-100 table-header">
                      <tr>
                        <th className="text-gray-700 table-header-cell">
                          Bond Name
                        </th>
                        <th className="text-gray-700 table-header-cell">
                          Old Amount
                        </th>
                        <th className="text-gray-700 table-header-cell">
                          New Amount
                        </th>
                        <th className="text-gray-700 table-header-cell">
                          Requested By
                        </th>
                        <th className="text-gray-700 table-header-cell">
                          Requested Date
                        </th>
                        <th className="text-gray-700 table-header-cell">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingChangeRequestsData.map((changeRequest) => {
                        const requestedByName =
                          changeRequest.requestedBy?.corporateInfo
                            ?.companyName ||
                          [
                            changeRequest.requestedBy?.personalInfo?.firstName,
                            changeRequest.requestedBy?.personalInfo?.lastName,
                          ]
                            .filter(Boolean)
                            .join(" ") ||
                          changeRequest.requestedBy?.email ||
                          "Unknown";
                        return (
                          <tr key={changeRequest._id}>
                            <td className="px-4 py-3 font-medium text-gray-900">
                              {changeRequest.bondName || "Unknown Bond"}
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-700">
                              {formatCurrency(changeRequest.oldAmount || 0)}
                            </td>
                            <td className="px-4 py-3 font-semibold text-gray-900">
                              {formatCurrency(changeRequest.newAmount || 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {requestedByName}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {changeRequest.requestedAt
                                ? formatDate(changeRequest.requestedAt)
                                : "—"}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() =>
                                    approveChangeRequestMutation.mutate(
                                      changeRequest._id
                                    )
                                  }
                                  disabled={
                                    approveChangeRequestMutation.isPending ||
                                    rejectChangeRequestMutation.isPending
                                  }
                                  className="btn-primary btn-sm"
                                  title="Approve"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() =>
                                    rejectChangeRequestMutation.mutate(
                                      changeRequest._id
                                    )
                                  }
                                  disabled={
                                    approveChangeRequestMutation.isPending ||
                                    rejectChangeRequestMutation.isPending
                                  }
                                  className="btn-secondary btn-sm"
                                  title="Reject"
                                >
                                  Reject
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* LOC Requests Table */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between w-full">
                <h3>LOC Requests</h3>
                <button className="btn-primary btn-sm">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </button>
              </div>
            </div>
            <div className="p-0 card-body">
              <div className="overflow-x-auto">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Bond Name</th>
                      <th className="table-header-cell">ISIN</th>
                      <th className="table-header-cell">Stock Code</th>
                      <th className="table-header-cell">Requested Amount</th>
                      <th className="table-header-cell">Allocated Amount</th>
                      <th className="table-header-cell">Rate</th>
                      <th className="table-header-cell">Issue Date</th>
                      <th className="table-header-cell">Maturity Date</th>
                      <th className="table-header-cell">Status</th>
                      <th className="table-header-cell">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedRequests.map((request) => {
                      const rateValue = getRateValue(request);
                      return (
                        <tr
                          key={request.id}
                          className="transition-colors border-b border-light-gray hover:bg-secondary-50"
                        >
                          <td className="px-4 py-4">
                            <div>
                              <div className="text-sm font-semibold leading-5 text-dark-gray-900">
                                {request.bondName}
                              </div>
                              <div className="mt-1 text-xs text-secondary-500">
                                {request.issuer} |{" "}
                                {getBondTypeDisplay(request.bondType)}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 font-mono text-sm text-secondary-600">
                            {request.isin || "—"}
                          </td>
                          <td className="px-4 py-4 font-mono text-sm text-secondary-600">
                            {request.stockCode || "—"}
                          </td>
                          <td className="px-4 py-4 text-sm text-secondary-600">
                            {formatCurrency(request.amount)}
                          </td>
                          <td className="px-4 py-4 text-sm text-secondary-600">
                            {request.allocatedAmount != null ? (
                              <div className="flex flex-col">
                                <span className="font-semibold text-gray-900">
                                  {formatCurrency(request.allocatedAmount)}
                                </span>
                                {request.allocatedAmount !== request.amount && (
                                  <span className="text-xs">
                                    {request.allocatedAmount > request.amount ? (
                                      <span className="text-green-600">
                                        +{formatCurrency(request.allocatedAmount - request.amount)}
                                      </span>
                                    ) : (
                                      <span className="text-amber-600">
                                        {formatCurrency(request.allocatedAmount - request.amount)}
                                      </span>
                                    )}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-secondary-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-secondary-600">
                            {rateValue != null ? `${rateValue}%` : "—"}
                          </td>
                          <td className="px-4 py-4 text-sm text-secondary-600">
                            {request.issueDate
                              ? formatDate(request.issueDate)
                              : "—"}
                          </td>
                          <td className="px-4 py-4 text-sm text-secondary-600">
                            {request.maturityDate
                              ? formatDate(request.maturityDate)
                              : "—"}
                          </td>
                          <td className="px-4 py-4">
                            {getStatusBadge(request.status)}
                          </td>
                          <td className="px-4 py-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              {canApproveL1(request) && (
                                <button
                                  onClick={() =>
                                    handleApprovalRequest(request, "L1")
                                  }

                                  className="btn-primary btn-sm whitespace-nowrap"
                                  title="Approve L1"
                                >
                                  Approve L1
                                </button>
                              )}
                              {canApproveL2(request) && (
                                <button
                                  onClick={() =>
                                    handleApprovalRequest(request, "L2")
                                  }

                                  className="btn-primary btn-sm whitespace-nowrap"
                                  title="Approve L2"
                                >
                                  Approve L2
                                </button>
                              )}
                              <button
                                onClick={() =>
                                  handleViewRequestDetails(request)
                                }

                                className="inline-flex items-center justify-center w-8 h-8 transition-colors rounded-lg hover:bg-secondary-200 text-primary-600 hover:text-secondary-700"
                                title="View Request Details"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {canDeleteRequest(request) && (
                                <button
                                  onClick={() => handleDeleteRequest(request)}

                                  className="inline-flex items-center justify-center w-8 h-8 text-red-600 transition-colors rounded-lg hover:bg-red-100 hover:text-red-700"
                                  title="Delete Request"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {!requestsData.length && (
                      <tr>
                        <td
                          colSpan="10"
                          className="py-12 text-center text-secondary-500"
                        >
                          <div className="flex flex-col items-center space-y-2">
                            <FileText className="w-10 h-10 text-gray-300" />
                            <p className="text-sm">No LOC requests yet</p>                           
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {requestsData.length > 0 && (
                  <div className="px-6 py-3 border-t bg-secondary-50 border-light-gray">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-secondary-500">
                        <span>
                          Showing{" "}
                          {requestsData.length === 0 ? 0 : startIndex + 1} to{" "}
                          {Math.min(endIndex, totalItems)} of {totalItems}{" "}
                          results
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="p-2 bg-white border rounded-md border-secondary-300 text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>

                        {Array.from(
                          { length: totalPages },
                          (_, i) => i + 1
                        ).map((pageNum) => (
                          <button
                            key={pageNum}
                            onClick={() => setPage(pageNum)}
                            className={`px-3 py-1 rounded-md text-sm font-medium ${pageNum === currentPage
                                ? "bg-primary-600 text-white"
                                : "bg-white border border-secondary-300 text-secondary-700 hover:bg-secondary-50"
                              }`}
                          >
                            {pageNum}
                          </button>
                        ))}

                        <button
                          onClick={() =>
                            setPage((p) => Math.min(totalPages, p + 1))
                          }
                          disabled={currentPage === totalPages}
                          className="p-2 bg-white border rounded-md border-secondary-300 text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Request Details Modal */}
          {showRequestDetails && selectedRequest && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white rounded-lg w-full max-w-3xl mx-4 sm:mx-6 lg:mx-0 max-h-[90vh] overflow-y-auto shadow-xl">
                <div className="flex items-start justify-between px-6 py-5 border-b border-light-gray">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      LOC Request Details
                    </h3>
                    <p className="mt-1 text-sm text-secondary-600">
                      Review the history of LOC submissions and requested
                      amounts for this bond.
                    </p>
                  </div>
                  <button
                    onClick={handleCloseRequestDetails}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close request details"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="px-6 py-6 space-y-6">
                  <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 text-secondary-700">
                    <div>
                      <span className="block font-medium text-secondary-500">
                        Bond
                      </span>
                      <span className="font-semibold text-gray-900">
                        {selectedRequest.bondName}
                      </span>
                    </div>
                    <div>
                      <span className="block font-medium text-secondary-500">
                        Status
                      </span>
                      {getStatusBadge(selectedRequest.status)}
                    </div>
                    <div>
                      <span className="block font-medium text-secondary-500">
                        Total Requested
                      </span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(selectedRequest.amount)}
                      </span>
                    </div>
                    {selectedRequest.allocatedAmount != null && (
                      <div>
                        <span className="block font-medium text-secondary-500">
                          Allocated Amount
                        </span>
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(selectedRequest.allocatedAmount)}
                          </span>
                          {selectedRequest.allocatedAmount !== selectedRequest.amount && (
                            <span className="text-xs">
                              {selectedRequest.allocatedAmount > selectedRequest.amount ? (
                                <span className="text-green-600">
                                  +{formatCurrency(selectedRequest.allocatedAmount - selectedRequest.amount)} (Increased)
                                </span>
                              ) : (
                                <span className="text-amber-600">
                                  {formatCurrency(selectedRequest.allocatedAmount - selectedRequest.amount)} (Reduced)
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    <div>
                      <span className="block font-medium text-secondary-500">
                        Latest Submission
                      </span>
                      <span className="text-gray-900">
                        {selectedRequest.latestSubmissionAt
                          ? formatDate(selectedRequest.latestSubmissionAt)
                          : "—"}
                      </span>
                    </div>
                    <div>
                      <span className="block font-medium text-secondary-500">
                        Approval Status
                      </span>
                      {getApprovalStatusBadge(selectedRequest)}
                    </div>
                    {selectedRequest.l1ApprovedAt && (
                      <div>
                        <span className="block font-medium text-secondary-500">
                          L1 Approved
                        </span>
                        <span className="text-gray-900">
                          {formatDate(selectedRequest.l1ApprovedAt)}
                        </span>
                      </div>
                    )}
                    {selectedRequest.l2ApprovedAt && (
                      <div>
                        <span className="block font-medium text-secondary-500">
                          L2 Approved
                        </span>
                        <span className="text-gray-900">
                          {formatDate(selectedRequest.l2ApprovedAt)}
                        </span>
                      </div>
                    )}
                    {selectedRequest.rejectedAt && (
                      <>
                        <div>
                          <span className="block font-medium text-secondary-500">
                            Rejected
                          </span>
                          <span className="text-gray-900">
                            {formatDate(selectedRequest.rejectedAt)}
                          </span>
                        </div>
                        {selectedRequest.rejectionReason && (
                          <div className="sm:col-span-2">
                            <span className="block font-medium text-secondary-500">
                              Rejection Reason
                            </span>
                            <span className="text-gray-900">
                              {selectedRequest.rejectionReason}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div>
                    <h4 className="mb-3 text-sm font-medium text-gray-700">
                      Submission History
                    </h4>
                    {selectedRequest.submissions &&
                      selectedRequest.submissions.length > 0 ? (
                      <div className="overflow-x-auto border rounded-md border-light-gray">
                        <table className="min-w-full text-sm table-auto">
                          <thead className="text-xs tracking-wide uppercase bg-secondary-50 text-secondary-600">
                            <tr>
                              <th className="px-4 py-3 text-left">
                                Submitted On
                              </th>
                              <th className="px-4 py-3 text-left">
                                Bond Amount
                              </th>
                              <th className="px-4 py-3 text-left">
                                LOC Document
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-light-gray">
                            {selectedRequest.submissions.map(
                              (submission, index) => (
                                <tr
                                  key={`${selectedRequest.requestId}-submission-${index}`}
                                  className="bg-white"
                                >
                                  <td className="px-4 py-3 text-gray-700">
                                    {submission.submittedAt
                                      ? formatDate(submission.submittedAt)
                                      : "—"}
                                  </td>
                                  <td className="px-4 py-3 text-gray-700">
                                    {formatCurrency(submission.amount || 0)}
                                  </td>
                                  <td className="px-4 py-3 text-secondary-700">
                                    <div className="flex items-center space-x-2">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handlePreviewSubmissionLoc(submission)
                                        }
                                        className="btn-secondary btn-sm"
                                        disabled={!submission.locFileId}
                                      >
                                        Preview
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleDownloadSubmissionLoc(
                                            submission
                                          )
                                        }
                                        className="btn-primary btn-sm"
                                        disabled={
                                          !submission.locFileId ||
                                          submissionDownloadId ===
                                          submission.locFileId
                                        }
                                      >
                                        {submissionDownloadId ===
                                          submission.locFileId
                                          ? "Downloading…"
                                          : "Download"}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-secondary-600">
                        No submissions recorded yet.
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex justify-end px-6 py-4 border-t border-light-gray">
                  <button
                    type="button"
                    onClick={handleCloseRequestDetails}
                    className="btn-secondary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Subscription Modal */}
          {showSubscribeModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white rounded-lg w-full max-w-3xl mx-4 sm:mx-6 lg:mx-0 max-h-[90vh] overflow-y-auto shadow-xl">
                <div className="flex items-start justify-between px-6 py-5 border-b border-light-gray">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Submit LOC
                    </h3>
                    <p className="mt-1 text-sm text-secondary-600">
                      Select an eligible bond, specify the desired bond amount,
                      and provide the Letter of Commitment (LOC).
                    </p>
                  </div>
                  <button
                    onClick={handleCloseSubscriptionModal}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close subscription modal"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="px-6 py-6 space-y-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Select Bond
                    </label>
                    <select
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      value={subscriptionBondId}
                      onChange={(e) => {
                        setSubscriptionBondId(e.target.value);
                        setSubscriptionPrimarySubscriberId("");
                      }}
                    >
                      <option value="">
                        {availableBondsLoading
                          ? "Loading eligible bonds..."
                          : eligibleBonds.length === 0
                            ? "No eligible bonds available"
                            : "Choose a bond"}
                      </option>
                      {eligibleBonds.map((bond) => (
                        <option
                          key={bond._id || bond.id}
                          value={bond._id || bond.id}
                        >
                          {bond.bondName || bond.name || "Unnamed Bond"}
                        </option>
                      ))}
                    </select>
                  </div>

                  {subscriptionBondId && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Select Primary Subscriber
                      </label>
                      <select
                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        value={subscriptionPrimarySubscriberId}
                        onChange={(e) =>
                          setSubscriptionPrimarySubscriberId(e.target.value)
                        }
                        disabled={primarySubscribersLoading}
                      >
                        <option value="">
                          {primarySubscribersLoading
                            ? "Loading primary subscribers..."
                            : primarySubscribers.length === 0
                              ? "No primary subscribers available"
                              : "Choose a primary subscriber"}
                        </option>
                        {primarySubscribers.map((ps) => (
                          <option key={ps.id} value={ps.id}>
                            {ps.name || ps.email}
                          </option>
                        ))}
                      </select>
                      {primarySubscribers.length === 0 &&
                        !primarySubscribersLoading &&
                        subscriptionBondId && (
                          <p className="mt-1 text-xs text-error-600">
                            No LOC templates available for this bond. Please
                            contact a primary subscriber.
                          </p>
                        )}
                    </div>
                  )}

                  {locTemplateLoading &&
                    subscriptionBondId &&
                    subscriptionPrimarySubscriberId && (
                      <div className="flex items-center justify-center py-4">
                        <div className="w-6 h-6 border-b-2 rounded-full animate-spin border-primary-600"></div>
                        <span className="ml-2 text-sm text-gray-600">
                          Loading template...
                        </span>
                      </div>
                    )}

                  {locTemplate && !locTemplateLoading && (
                    <div className="space-y-4">
                      <div className="pt-4 border-t border-gray-200">
                        <h4 className="mb-4 text-sm font-medium text-gray-900">
                          LOC Form Fields
                        </h4>
                        <div className="space-y-4">
                          {(locTemplate.fields || [])
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .map((field) => (
                              <div key={field.name} className="space-y-1">
                                <label className="block text-sm font-medium text-gray-700">
                                  {field.label}
                                  {field.required && (
                                    <span className="ml-1 text-error-600">
                                      *
                                    </span>
                                  )}
                                </label>
                                {field.type === "text" && (
                                  <input
                                    type="text"
                                    value={locFieldData[field.name] || ""}
                                    onChange={(e) =>
                                      handleFieldChange(
                                        field.name,
                                        e.target.value
                                      )
                                    }
                                    placeholder={field.placeholder || ""}
                                    required={field.required}
                                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  />
                                )}
                                {field.type === "number" && (
                                  <input
                                    type="number"
                                    value={locFieldData[field.name] || ""}
                                    onChange={(e) =>
                                      handleFieldChange(
                                        field.name,
                                        e.target.value
                                          ? Number(e.target.value)
                                          : ""
                                      )
                                    }
                                    placeholder={field.placeholder || ""}
                                    required={field.required}
                                    min={field.validation?.min}
                                    max={field.validation?.max}
                                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  />
                                )}
                                {field.type === "date" && (
                                  <input
                                    type="date"
                                    value={locFieldData[field.name] || ""}
                                    onChange={(e) =>
                                      handleFieldChange(
                                        field.name,
                                        e.target.value
                                      )
                                    }
                                    required={field.required}
                                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  />
                                )}
                                {field.type === "email" && (
                                  <input
                                    type="email"
                                    value={locFieldData[field.name] || ""}
                                    onChange={(e) =>
                                      handleFieldChange(
                                        field.name,
                                        e.target.value
                                      )
                                    }
                                    placeholder={field.placeholder || ""}
                                    required={field.required}
                                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  />
                                )}
                                {field.type === "textarea" && (
                                  <textarea
                                    value={locFieldData[field.name] || ""}
                                    onChange={(e) =>
                                      handleFieldChange(
                                        field.name,
                                        e.target.value
                                      )
                                    }
                                    placeholder={field.placeholder || ""}
                                    required={field.required}
                                    rows={4}
                                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  />
                                )}
                                {field.type === "dropdown" && (
                                  <select
                                    value={locFieldData[field.name] || ""}
                                    onChange={(e) =>
                                      handleFieldChange(
                                        field.name,
                                        e.target.value
                                      )
                                    }
                                    required={field.required}
                                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  >
                                    <option value="">Select an option</option>
                                    {(field.options || []).map(
                                      (option, idx) => (
                                        <option key={idx} value={option.value}>
                                          {option.label}
                                        </option>
                                      )
                                    )}
                                  </select>
                                )}
                                {field.type === "checkbox" && (
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={
                                        locFieldData[field.name] || false
                                      }
                                      onChange={(e) =>
                                        handleFieldChange(
                                          field.name,
                                          e.target.checked
                                        )
                                      }
                                      required={field.required}
                                      className="w-4 h-4 border-gray-300 rounded text-primary-600 focus:ring-primary-500"
                                    />
                                    <label className="ml-2 text-sm text-gray-700">
                                      {field.placeholder || "I agree"}
                                    </label>
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-medium text-gray-900">
                            LOC Document
                          </h4>
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={handlePreviewPDF}
                              disabled={pdfGenerating}
                              className="inline-flex items-center px-3 py-2 text-xs font-medium bg-white border border-gray-300 rounded-md text-secondary-600 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {pdfGenerating ? "Generating..." : "Preview PDF"}
                            </button>
                            <button
                              type="button"
                              onClick={handleDownloadPDF}
                              disabled={pdfGenerating}
                              className="inline-flex items-center px-3 py-2 text-xs font-medium text-white border rounded-md border-primary-600 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              {pdfGenerating ? "Generating..." : "Download PDF"}
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-secondary-500">
                          Fill in all required fields above, then preview or
                          download your LOC document.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Funding Bank Account
                    </label>
                    {walletsLoading ? (
                      <div className="text-sm text-secondary-600">
                        Loading linked bank accounts…
                      </div>
                    ) : investorWallets.length > 0 ? (
                      <select
                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        value={selectedWalletId}
                        onChange={(e) => setSelectedWalletId(e.target.value)}
                        disabled={
                          walletsLoading || investorWallets.length === 0
                        }
                      >
                        {investorWallets.length > 1 && (
                          <option value="">Select bank account</option>
                        )}
                        {investorWallets.map((wallet) => {
                          const bankName =
                            wallet.bankAccountDetails?.bankName ||
                            wallet.bankName ||
                            "Bank";
                          const accountNumber =
                            wallet.bankAccountDetails?.accountNumber ||
                            wallet.accountNumber ||
                            "—";
                          return (
                            <option key={wallet.id} value={wallet.id}>
                              {`${bankName} • ${accountNumber}`}
                            </option>
                          );
                        })}
                      </select>
                    ) : (
                      <div className="px-3 py-2 text-sm border border-gray-300 border-dashed rounded-md bg-secondary-50 text-secondary-600">
                        No linked bank accounts found. Please add one in Wallet
                        Management before submitting an LOC request.
                      </div>
                    )}
                    <p className="text-xs text-secondary-500">
                      Only the bank name and account number are visible. Wallet
                      address and LEI are saved with your request.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Bond Amount
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      placeholder="Enter desired bond amount"
                      value={subscriptionAmount}
                      onChange={(e) => setSubscriptionAmount(e.target.value)}
                    />
                    <p className="text-xs text-secondary-500">
                      Specify the number of tokens you wish to subscribe for
                      this bond.
                    </p>
                  </div>

                  <div className="flex items-start px-3 py-3 space-x-3 bg-white border border-gray-200 rounded-md">
                    <input
                      id="investor-agree-terms"
                      type="checkbox"
                      className="h-4 w-4 mt-0.5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      checked={hasAgreedToTerms}
                      onChange={(e) => setHasAgreedToTerms(e.target.checked)}
                    />
                    <label
                      htmlFor="investor-agree-terms"
                      className="text-sm text-gray-700"
                    >
                      I have read and agree to the platform terms &amp;
                      conditions.
                      <span className="block mt-1 text-xs text-secondary-500">
                        Please review your information carefully before
                        submitting this LOC request.
                      </span>
                    </label>
                  </div>
                </div>
                <div className="flex flex-col px-6 py-4 space-y-3 border-t border-light-gray sm:flex-row sm:justify-end sm:space-x-3 sm:space-y-0">
                  <button
                    type="button"
                    onClick={handleCloseSubscriptionModal}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitSubscription}
                    className="btn-primary"
                    disabled={
                      !subscriptionBondId ||
                      !subscriptionAmount ||
                      !subscriptionPrimarySubscriberId ||
                      !locTemplate ||
                      !selectedWallet ||
                      !hasAgreedToTerms ||
                      isSubmittingRequest ||
                      createRequestMutation.isPending
                    }
                    title="Submit Request"
                  >
                    {isSubmittingRequest || createRequestMutation.isPending
                      ? "Submitting…"
                      : "Submit Request"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {deleteConfirmation && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="w-full max-w-md mx-4 bg-white rounded-lg shadow-xl">
                <div className="px-6 py-5 border-b border-light-gray">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Confirm Delete
                  </h3>
                </div>
                <div className="px-6 py-6">
                  <p className="text-sm text-gray-700">
                    Are you sure you want to delete your allocation request for{" "}
                    <strong>{deleteConfirmation.bondName}</strong>?
                  </p>
                  <p className="mt-3 text-sm text-red-600">
                    This action cannot be undone.
                  </p>
                </div>
                <div className="flex justify-end px-6 py-4 space-x-3 border-t border-light-gray">
                  <button
                    type="button"
                    onClick={handleCancelDelete}
                    className="btn-secondary"
                    disabled={deleteRequestMutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 text-sm font-medium text-white transition-colors bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={deleteRequestMutation.isPending}
                  >
                    {deleteRequestMutation.isPending ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Approval Confirmation Modal */}
          {approvalConfirmation && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="w-full max-w-md mx-4 bg-white rounded-lg shadow-xl">
                <div className="px-6 py-5 border-b border-light-gray">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Confirm {approvalConfirmation.level} Approval
                  </h3>
                </div>
                <div className="px-6 py-6">
                  <p className="text-sm text-gray-700">
                    Are you sure you want to approve{" "}
                    {approvalConfirmation.level} for the allocation request for{" "}
                    <strong>{approvalConfirmation.bondName}</strong>?
                  </p>
                  {approvalConfirmation.level === "L1" && (
                    <p className="mt-3 text-sm text-gray-600">
                      This will move the request to Pending L2 status.
                    </p>
                  )}
                  {approvalConfirmation.level === "L2" && (
                    <p className="mt-3 text-sm text-gray-600">
                      This will submit the request to the Primary Subscriber for
                      processing.
                    </p>
                  )}
                </div>
                <div className="flex justify-end px-6 py-4 space-x-3 border-t border-light-gray">
                  <button
                    type="button"
                    onClick={handleCancelApproval}
                    className="btn-secondary"
                    disabled={
                      approveL1Mutation.isPending || approveL2Mutation.isPending
                    }
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmApproval}
                    className="btn-primary"
                    disabled={
                      approveL1Mutation.isPending || approveL2Mutation.isPending
                    }
                  >
                    {approveL1Mutation.isPending || approveL2Mutation.isPending
                      ? "Approving..."
                      : `Approve ${approvalConfirmation.level}`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionRequests;
